import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, User, CheckCircle2, AlertCircle, LayoutGrid, Hourglass } from 'lucide-react';
import { usePortalAuth } from '../../context/PortalAuthContext';
import PortalLayout from './PortalLayout';
import { Button } from '../../components/ui/button';

const PortalHome = () => {
    const { t } = useTranslation('portal');
    const { profile, loading, email } = usePortalAuth();
    const complete = profile?.profile_complete;
    const approved = profile?.is_approved !== false;
    const name = profile?.contact_name || profile?.company_name || email?.split('@')[0];

    return (
        <PortalLayout>
            <div className="space-y-8" data-testid="portal-home">
                {/* Greeting */}
                <div>
                    <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-2">{t('home.welcome_back')}</p>
                    <h1 className="text-3xl font-bold text-[#1A1A1A]">
                        {loading ? '…' : name || t('home.default_name')}
                    </h1>
                </div>

                {/* Account-under-review banner — only shown when admin hasn't approved yet */}
                {!loading && !approved && (
                    <div
                        className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-lg p-5"
                        data-testid="account-under-review-banner"
                    >
                        <Hourglass className="text-amber-700 mt-0.5" size={20} />
                        <div className="flex-1">
                            <h3 className="font-semibold text-amber-900 mb-1">{t('home.review_title')}</h3>
                            <p className="text-sm text-amber-800">{t('home.review_body')}</p>
                        </div>
                    </div>
                )}

                {/* Profile completeness banner */}
                {!loading && !complete && (
                    <div
                        className="flex items-start gap-4 bg-white border border-[#EFE5E1] rounded-lg p-5"
                        data-testid="profile-incomplete-banner"
                    >
                        <AlertCircle className="text-[#C08478] mt-0.5" size={20} />
                        <div className="flex-1">
                            <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('home.incomplete_title')}</h3>
                            <p className="text-sm text-gray-500 mb-4">{t('home.incomplete_body')}</p>
                            <Button asChild className="bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-5">
                                <Link to="/portal/profile" data-testid="profile-complete-cta">{t('home.incomplete_cta')}</Link>
                            </Button>
                        </div>
                    </div>
                )}

                {!loading && complete && (
                    <div
                        className="flex items-start gap-4 bg-white border border-[#EFE5E1] rounded-lg p-5"
                        data-testid="profile-complete-banner"
                    >
                        <CheckCircle2 className="text-emerald-600 mt-0.5" size={20} />
                        <div className="flex-1">
                            <h3 className="font-semibold text-[#1A1A1A]">{t('home.complete_title')}</h3>
                            <p className="text-sm text-gray-500">
                                {approved ? t('home.ready_body') : t('home.complete_body')}
                            </p>
                            {approved && (
                                <Button asChild className="mt-3 bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full px-5">
                                    <Link to="/portal/catalog" data-testid="profile-complete-browse-cta">{t('home.ready_cta')}</Link>
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* Tile grid */}
                <div className="grid sm:grid-cols-3 gap-4">
                    <Link
                        to="/portal/catalog"
                        className="bg-white border border-[#EFE5E1] rounded-lg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
                        data-testid="portal-tile-catalog"
                    >
                        <LayoutGrid size={28} className="text-[#C08478] mb-3" />
                        <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('home.tile_catalog_title')}</h3>
                        <p className="text-sm text-gray-500">{t('home.tile_catalog_body')}</p>
                    </Link>
                    <Link
                        to="/portal/profile"
                        className="bg-white border border-[#EFE5E1] rounded-lg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
                        data-testid="portal-tile-profile"
                    >
                        <User size={28} className="text-[#C08478] mb-3" />
                        <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('home.tile_profile_title')}</h3>
                        <p className="text-sm text-gray-500">{t('home.tile_profile_body')}</p>
                    </Link>
                    <Link
                        to="/portal/orders"
                        className="bg-white border border-[#EFE5E1] rounded-lg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all"
                        data-testid="portal-tile-orders"
                    >
                        <ShoppingBag size={28} className="text-[#C08478] mb-3" />
                        <h3 className="font-semibold text-[#1A1A1A] mb-1">{t('home.tile_orders_title')}</h3>
                        <p className="text-sm text-gray-500">{t('home.tile_orders_body')}</p>
                    </Link>
                </div>
            </div>
        </PortalLayout>
    );
};

export default PortalHome;
